import React, { useState, useEffect } from 'react';
import { useRedux } from 'services/gecko-store/redux';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import GeckoLocation from 'services/gecko-location';
import { GeckoToastService, GeckoDialogService } from 'ui';
import BotTabEditContainer from '../BotTabEditContainer';
import { useUnsavedChanges } from 'components/UnsavedChanges';
import { CONSTS } from 'constants/index.js';

import SkillDisplayList from '../SkillDisplayList';
import SkillSuggestAnswer from '../SkillSuggestAnswer';
import SkillGeckoForm from '../SkillGeckoForm';
import SkillWorkflowAction from '../SkillWorkflowAction';
import SkillDataCapture from '../SkillDataCapture';

import qs from '../../../utilities/queryString.js';

import './index.scss';

function SkillPage({ botId, id, type, cloning }) {
  const [skill, setSkill] = useState();
  const agents = useRedux((state) => state.agents);
  const [clearUnsavedChanges, , setInitialValue, isDirty] = useUnsavedChanges([
    skill,
  ]);

  useEffect(() => {
    if (!id || id === 'new') {
      return;
    }
    GeckoRequest.get(`/bots/${botId}/skills/${cloning ? 'clone/' : ''}${id}`)
      .then((_skill) => {
        setInitialValue([_skill]);
        setSkill(_skill);
      })
      .catch(renderError);
  }, [id, cloning]);

  const skillForms = {
    display_list: (
      <SkillDisplayList
        key={skill && skill.id}
        value={skill}
        onChange={setSkill}
        botId={botId}
      />
    ),
    suggest_answer: (
      <SkillSuggestAnswer
        key={skill && skill.id}
        value={skill}
        onChange={setSkill}
        botId={botId}
      />
    ),
    gecko_form: (
      <SkillGeckoForm
        key={skill && skill.id}
        value={skill}
        onChange={setSkill}
        botId={botId}
      />
    ),
    workflow_action: (
      <SkillWorkflowAction
        key={skill && skill.id}
        value={skill}
        onChange={setSkill}
        botId={botId}
      />
    ),
    data_capture: (
      <SkillDataCapture
        key={skill && skill.id}
        value={skill}
        onChange={setSkill}
        botId={botId}
      />
    ),
  };
  const skillForm = skillForms[type] || null;

  const getRedirectUrl = () => {
    const params = qs.parse(GeckoLocation.location.search);
    return params['answer_id']
      ? `/bots/${botId}/answers/${params['answer_id']}`
      : `/bots/${botId}/skills`;
  };

  const saveSkill = () => {
    if (id === 'new' || cloning) {
      GeckoRequest.post(`/bots/${botId}/skills`, skill)
        .then(() => {
          clearUnsavedChanges();
          new GeckoToastService().success('New Skill saved');
          GeckoLocation.goTo(getRedirectUrl());
        })
        .catch(renderError);
    } else {
      GeckoRequest.post(`/bots/${botId}/skills/${id}`, skill)
        .then(() => {
          clearUnsavedChanges();
          new GeckoToastService().success('Skill updated');
          GeckoLocation.goTo(getRedirectUrl());
        })
        .catch(renderError);
    }
  };

  const handleCancel = () => {
    clearUnsavedChanges();
    if (id && id !== 'new') {
      GeckoLocation.goTo(getRedirectUrl());
    } else {
      GeckoLocation.goTo(`/bots/${botId}/skills/new`);
    }
  };

  const handleClone = () => {
    return GeckoLocation.goTo(
      `/bots/${botId}/skills/${
        CONSTS.BOT_SKILL_HANDOVER[skill.internal_skill] ||
        skill.internal_skill.toLowerCase()
      }/clone/${skill.id}`
    );
  };

  const handleDelete = () => {
    return new GeckoDialogService()
      .confirm(`Are you sure you want to delete this skill?`)
      .then((response) => {
        if (response) {
          return GeckoRequest.delete(`/bots/${botId}/skills/${id}`)
            .then(() => {
              clearUnsavedChanges();
              new GeckoToastService().success('Skill deleted');
              return GeckoLocation.goTo(`/bots/${botId}/skills`);
            })
            .catch(renderError);
        }
        return false;
      })
      .catch(renderError);
  };

  const shouldRenderPreview = () => {
    if (id && id === 'new') {
      return true;
    }
    if (id && !skill) {
      return false;
    }
    return true;
  };

  const typeMap = {
    display_list: 'display list',
    suggest_answer: 'suggest answer',
    gecko_form: 'Gecko Form',
    workflow_action: 'workflow action',
    data_capture: 'data capture',
  };
  return (
    <div className="SkillPage">
      <BotTabEditContainer
        key={skill && skill.id}
        title={`${
          id === 'new'
            ? 'Add a New Skill'
            : `Edit your ${typeMap[type] || type} skill`
        }`}
        updated={{
          by:
            skill?.last_updated_by &&
            agents.find((agent) => agent.id === skill.last_updated_by),
          at: skill?.updatedAt,
        }}
        saveText="Save Skill"
        onSave={saveSkill}
        onCancel={handleCancel}
        onClone={!cloning && id !== 'new' ? handleClone : null}
        onDelete={!cloning && id !== 'new' ? handleDelete : null}
        disableSave={!isDirty}
      >
        <div className="row">
          <div className="SkillPage-skill-form col-12">{skillForm}</div>
        </div>
      </BotTabEditContainer>
    </div>
  );
}

export default SkillPage;
